import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPlantillasSemanales } from '../../services/api/plantillaSemanalService';
import { useCreateMultipleEventosMutation } from '../../services/hooks';
import { useClassroomsQuery } from '../../services/hooks/useClassrooms';
import { PlantillaSemanal, Evento, Festivo } from '../../types/index';
import { toast } from 'react-toastify';
import { X, Eye } from 'lucide-react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getEventBackgroundColor } from '../../utils/utils';
import { clasesSinReferencia, refNombre } from '../../utils/plantillaRefs';

interface EventosMasivosProps {
        onClose: () => void;
        startDate: string;
        endDate: string;
        festivos: Festivo[]; 
    }

interface NewEvento {
    nivel_id: string;
    instructor_id: string;
    dia: string;
    sala: string;
    horaInicio: string;
    horaFin: string;
}

interface CrearEventosMasivosParams {
    plantillaIds: string[];  // Array de IDs de plantillas
    fechaInicio: Date;
    fechaFin: Date;
}

const EventosMasivos: React.FC<EventosMasivosProps> = ({ onClose, startDate, endDate, festivos }) => {
  // Query para obtener plantillas
    const { data: plantillas, isLoading, isError } = useQuery<PlantillaSemanal[]>({
        queryKey: ['plantillasSemanales'],
        queryFn: fetchPlantillasSemanales,
    });

    // Estado para controlar la expansión de cada plantilla
    const [expandedId, setExpandedId] = useState<string | null>(null);

    // Estado para las plantillas seleccionadas
    const [selectedPlantillas, setSelectedPlantillas] = useState(new Set());

    // Estado para mostrar el formulario de fechas
    const [fechaInicio, setFechaInicio] = useState<Date | null>(null);
    const [fechaFin, setFechaFin] = useState<Date | null>(null);

    // Barra de progreso
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);

    // Expande y contrae la plantilla
    const toggleExpand = (id: string) => {
        setExpandedId(prevId => (prevId === id ? null : id));
    };


    // Mutación para crear eventos masivos
    const createMultipleEventosMutation =  useCreateMultipleEventosMutation(startDate, endDate);

    // Salas del centro: de aquí sale el aforo de cada clase generada.
    const { data: classrooms } = useClassroomsQuery();


    // Función auxiliar para calcular la fecha específica según el día de la semana
    const calcularFechaEvento = (fechaSemana: Date, diaEvento: string): Date => {
        const diasSemana: Record<string, number> = {
            'LUNES': 1,
            'MARTES': 2,
            'MIERCOLES': 3,
            'MIÉRCOLES': 3, // Añadimos versión con acento
            'JUEVES': 4,
            'VIERNES': 5,
            'SABADO': 6,
            'SÁBADO': 6, // Por si acaso también
            'DOMINGO': 0
        };

        const fecha = new Date(fechaSemana);
        const diaActual = fecha.getDay();
        const diaObjetivo = diasSemana[diaEvento.toUpperCase()];
        const diferenciaDias = diaObjetivo - diaActual;
        // console.log('Fecha Semana:', fechaSemana);
        // console.log('Dia Actual:', diaActual);
        // console.log('Diferencia de días:', diferenciaDias);

        
        fecha.setDate(fecha.getDate() + diferenciaDias);
        // console.log('Fecha return:', fecha);
        return fecha;
    };


    //Función para crear eventos masivos - CORREGIDA
    const crearEventosMasivos = async ({
        plantillaIds,
        fechaInicio,
        fechaFin
    }: CrearEventosMasivosParams) => {

        setIsProcessing(true);
        setProgress(0);

        try {
            // Obtener las plantillas
            const plantillasSeleccionadas = plantillas?.filter(
                plantilla => plantillaIds.includes(plantilla._id!)
            ) || [];

            if (plantillasSeleccionadas.length === 0) {
                throw new Error('No se encontraron las plantillas seleccionadas');
            }

            // Sin las salas cargadas no se puede resolver el aforo, y el aviso de
            // "sala sin configurar" de más abajo sería engañoso.
            if (!classrooms) {
                toast.error('Todavía se están cargando las salas del centro. Inténtalo de nuevo en un momento.');
                return;
            }

            // El aforo de cada clase sale de la configuración de salas del centro
            // (colección classrooms), no de un número fijo. La plantilla semanal solo
            // guarda el NOMBRE de la sala, así que se resuelve por nombre.
            const aforoPorSala = new Map(classrooms.map(sala => [sala.name, sala.capacity]));
            const salasSinAforo = new Set<string>();

            const eventos: Evento[] = [];
            const fechaIteracion = new Date(fechaInicio);
            const fechaLimite = new Date(fechaFin);
            let indiceAlternancia = 0;

            // Calcular el total de semanas para la barra de progreso
            const totalSemanas = Math.ceil((fechaLimite.getTime() - fechaIteracion.getTime()) / (7 * 24 * 60 * 60 * 1000));
            let semanasProcesadas = 0;

            while (fechaIteracion <= fechaLimite) {
                // Determinar qué plantilla usar
                const plantillaActual = plantillasSeleccionadas[indiceAlternancia % plantillasSeleccionadas.length];

                if (!plantillaActual || !plantillaActual.eventos || plantillaActual.eventos.length === 0) {
                    throw new Error('La plantilla seleccionada no tiene eventos disponibles');
                }

                // Procesar cada evento de la plantilla
                plantillaActual.eventos.forEach(eventoPlantilla => {
                    const fecha = calcularFechaEvento(fechaIteracion, eventoPlantilla.dia);
                    
                    // VALIDACIÓN AGREGADA: Solo crear eventos >= fecha de inicio y <= fecha fin
                    if (fecha >= fechaInicio && fecha <= fechaLimite) {
                        // Normalizar la fecha del evento a hora local de Madrid
                        const fechaLocal = new Date(fecha.getTime() - (fecha.getTimezoneOffset() * 60000));
                        const fechaStr = fechaLocal.toISOString().split('T')[0];
                        
                        // Normalizar las fechas de los festivos de la misma manera
                        const esFestivo = festivos.some(festivo => {
                            const festivoDate = new Date(festivo.fecha);
                            const festivoLocal = new Date(festivoDate.getTime() - (festivoDate.getTimezoneOffset() * 60000));
                            return festivoLocal.toISOString().split('T')[0] === fechaStr;
                        });

                        if (!esFestivo) {
                            // Ajustando a fecha local en Madrid
                            const localDate = new Date(fecha.getTime() - (fecha.getTimezoneOffset() * 60000)).toISOString();

                            const aforo = aforoPorSala.get(eventoPlantilla.sala);
                            if (aforo === undefined) {
                                salasSinAforo.add(eventoPlantilla.sala);
                            }

                            eventos.push({
                                nivel_id: eventoPlantilla.nivel_id,
                                instructor_id: eventoPlantilla.instructor_id,
                                sala: eventoPlantilla.sala,
                                fecha: localDate,
                                horaInicio: eventoPlantilla.horaInicio,
                                horaFin: eventoPlantilla.horaFin,
                                maxClientes: aforo ?? 0,
                                cancelado: false,
                                clientesInscritos: []
                            });
                        } else {
                            console.log(`Saltando evento en día festivo: ${fechaStr}`);
                        }
                    } else {
                        console.log(`Saltando evento fuera del rango: ${fecha.toISOString().split('T')[0]} (debe estar entre ${fechaInicio.toISOString().split('T')[0]} y ${fechaLimite.toISOString().split('T')[0]})`);
                    }
                });
                
                // Avanzar a la siguiente semana
                fechaIteracion.setDate(fechaIteracion.getDate() + 7);
                
                // Si hay más de una plantilla, alternar
                if (plantillasSeleccionadas.length > 1) {
                    indiceAlternancia++;
                }

                // Actualizar progreso
                semanasProcesadas++;
                setProgress(Math.round((semanasProcesadas / totalSemanas) * 100));
            }

            // Parar ANTES de crear nada. Generar cientos de clases con un aforo
            // inventado es justo el fallo que trajo aquí: se prefiere fallar de
            // forma visible a dejar el horario mal en silencio.
            if (salasSinAforo.size > 0) {
                toast.error(
                    `No se generaron las clases: estas salas no están configuradas en el centro: ${[...salasSinAforo].join(', ')}. ` +
                    'Revísalas en Configuraciones → Salas y vuelve a intentarlo.'
                );
                return;
            }

            // Crear los eventos usando la mutación
            await createMultipleEventosMutation.mutateAsync(eventos);
            console.log('Eventos creados:', eventos);
            
            onClose();
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setIsProcessing(false);
            setProgress(0);
        }
    };

    //Función obtener rango de fechas y llamar a la función de crear eventos masivos
    const handleGenerarEventos = async (plantillaIds: string[]) => {
        if (!fechaInicio || !fechaFin) {
            toast.error('Por favor seleccione el rango de fechas');
            return;
        }
    
        
        const inicio = new Date(fechaInicio);
        const fin = new Date(fechaFin);
    
        if (inicio >= fin) {
            toast.error('La fecha de inicio debe ser menor a la fecha final');
            return;
        }

        // Una clase de la plantilla con nivel o instructor borrado haría fallar la
        // generación a mitad de proceso: se corta antes de crear nada.
        const sinReferencia = clasesSinReferencia(
            (plantillas ?? []).filter(plantilla => plantillaIds.includes(plantilla._id!))
        );
        if (sinReferencia.length > 0) {
            const ejemplos = sinReferencia.slice(0, 3).join(', ');
            const resto = sinReferencia.length > 3 ? '…' : '';
            toast.error(
                `Hay ${sinReferencia.length} ${sinReferencia.length === 1 ? 'clase' : 'clases'} en la plantilla con un nivel o instructor eliminado (${ejemplos}${resto}). Reasígnalas en Plantillas Semanales antes de generar.`,
                { autoClose: false }
            );
            return;
        }

        await crearEventosMasivos({
            plantillaIds,
            fechaInicio: inicio,
            fechaFin: fin
        });
    
        // Limpiar estados
        setSelectedPlantillas(new Set());
        setFechaInicio(null);
        setFechaFin(null);
    };

    // console.log(plantillas);



    if (isLoading) return <div>Cargando plantillas...</div>;
    if (isError) return <div>Error al cargar plantillas.</div>;

    return (
        <div className="modal-overlay fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="relative modal-content bg-white rounded-lg p-6 w-full max-w-4xl overflow-y-auto max-h-[80vh]">
                <button 
                    onClick={onClose} 
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                >
                    <X size={24} />
                </button>
                <div className="flex flex-col justify-between items-left mb-4">
                    <h2 className="text-2xl font-bold">Crear Eventos Masivos</h2>
                    <div className='p-2'>
                        <p>Usando Plantillas previamente creadas podrás crear eventos recurrentes en un rango de fechas.</p>
                        <p>Si no ves ninguna plantilla creada, primero ve al apartado correspondiente para crearlas.</p>
                    </div>
                </div>
                <div>
                    {plantillas?.map(plantilla => (
                        <div key={plantilla._id} className="border rounded p-2 mb-2">
                            <div className="flex justify-between items-center" >
                                <div className='flex justify-start gap-4 items-center'>
                                    <h3><strong>Plantilla Nro:</strong> {plantilla.numero_plantilla}</h3>
                                    <span><strong>Descripcion:</strong> {plantilla.descripcion}</span>
                                    <button 
                                        className={`text-xs px-3 py-1.5 rounded text-white transition-colors ${selectedPlantillas.has(plantilla._id)
                                            ? 'bg-green-600 hover:bg-green-700'
                                            : 'bg-blue-600 hover:bg-blue-700'}`}
                                        onClick={() => {
                                            // console.log('Plantilla ID:', plantilla._id); // Para ver el ID que intentamos agregar
                                            const newSelection = new Set(selectedPlantillas);
                                            
                                            if (newSelection.has(plantilla._id!)) {
                                                // console.log('Removiendo plantilla');
                                                newSelection.delete(plantilla._id!);
                                            } else if (newSelection.size < 2) {
                                                // console.log('Agregando plantilla');
                                                newSelection.add(plantilla._id!);
                                            }
                                            
                                            // console.log('Nueva selección:', newSelection);
                                            setSelectedPlantillas(newSelection);
                                        }}
                                    >
                                        {selectedPlantillas.has(plantilla._id) ? 'Seleccionada' : 'Usar Plantilla'}
                                    </button>
                                </div>
                                <div className="flex items-center gap-4 ml-auto">
                                        <Eye 
                                            className="cursor-pointer text-green-700"
                                            onClick={() => toggleExpand(plantilla._id!)}
                                        />
                                        {expandedId === plantilla._id! ? '▲' : '▼'}
                                    </div>
                            </div>
                            {expandedId === plantilla._id && (
                                <div className="mt-2 p-2 flex justify-start gap-4 bg-blue-50">
                                    {Object.entries(
                                        plantilla.eventos.reduce<Record<string, typeof plantilla.eventos>>((acc, evento) => {
                                            const dia = evento.dia;
                                            if (!acc[dia]) {
                                            acc[dia] = [];
                                            }
                                            acc[dia].push(evento);
                                            return acc;
                                        }, {})
                                        ).map(([dia, eventos]) => (
                                        <div key={dia}>
                                            <div className='bg-slate-500 rounded-lg m-1 text-white font-bold text-center'>
                                                {dia}
                                            </div>
                                            
                                            <ul>
                                            {eventos.map((evento, index) => {
                                                const backgroundColor = typeof evento.nivel_id === 'object' && evento.nivel_id !== null && 'nombre' in evento.nivel_id
                                                    ? getEventBackgroundColor(evento.nivel_id as { nombre: string; color?: string })
                                                    : '#6b7280'; // gray-500
                                                
                                                return (
                                                <li 
                                                    key={index} 
                                                    className="text-xs text-white rounded-lg mb-1 border-b p-2"
                                                    style={{ backgroundColor }}
                                                >
                                                    <p>
                                                        <strong>Nivel:</strong> {refNombre(evento.nivel_id) ?? <em className="font-bold underline">eliminado, reasígnalo</em>}
                                                    </p>
                                                    <p>
                                                        <strong>Instructor:</strong> {refNombre(evento.instructor_id) ?? <em className="font-bold underline">eliminado, reasígnalo</em>}
                                                    </p>
                                                    <p>
                                                        <strong>Sala:</strong> {evento.sala}
                                                    </p>
                                                    <p>
                                                        <strong>Hora:</strong> {evento.horaInicio} - {evento.horaFin}
                                                    </p>
                                                </li>
                                                );
                                            })}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                {selectedPlantillas.size > 0 && (
                    <div className='mb-4 p-4 border rounded'>
                        <h3 className="text-lg font-bold mb-2">Seleccione el rango de fechas</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Fecha Inicio:</label>
                                <DatePicker
                                    selected={fechaInicio}
                                    onChange={(date) => setFechaInicio(date)}
                                    dateFormat="dd/MM/yyyy"
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                    placeholderText="Seleccione fecha inicio"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Fecha Fin:</label>
                                <DatePicker
                                    selected={fechaFin}
                                    onChange={(date) => setFechaFin(date)}
                                    dateFormat="dd/MM/yyyy"
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                    placeholderText="Seleccione fecha fin"
                                    minDate={fechaInicio || undefined}
                                />
                            </div>
                        </div>
                        <button 
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded"
                            onClick={() => handleGenerarEventos(Array.from(selectedPlantillas) as string[])}
                        >
                            Generar Eventos ({selectedPlantillas.size === 1 ? 'Simple' : 'Alternados'})
                        </button>
                    </div>
                )}
                {isProcessing && (
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                        <div className="bg-white p-6 rounded-lg w-96">
                            <div className="mb-4 text-center font-bold">Procesando eventos...</div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                                <div 
                                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                                    style={{ width: `${progress}%` }}
                                ></div>
                            </div>
                            <div className="mt-2 text-center text-sm text-gray-600">
                                Progreso: {progress}%
                            </div>
                        </div>
                    </div>
                )}
                <div className="flex justify-end">
                    
                    <button className="mt-2 px-4 py-2 bg-red-500 text-white rounded" onClick={onClose}>
                        Cerrar
                    </button>
                </div>  
            </div>
        </div>
    );
};

export default EventosMasivos;